// ---------------------------------------------------------------------------
// Text gegen Angabe von Hand (PF 56): Was aus dem Text kommt, kann der Autor in der Seitenleiste selbst anders setzen – dann gilt seine
// Angabe. Sagt der Text etwas anderes, ist die Stelle im Text rot, mit „Aus dem Text übernehmen“ und „ist Absicht“ (wie die Zeitmarken,
// PLAN.md §16d). Geändert wird nichts ungefragt.
//  - Schauplatz: gesetzt, der Draft nennt verknüpfte Orte (außerhalb von Rede und Gedanken), aber nie ihn → an der ersten Ortsnennung
//    („Der Text nennt die Taverne, Schauplatz ist der Wald“). Ein Ortswechsel zum Schauplatz im Text ist kein Widerspruch.
//  - Perspektive: gesetzt, der Draft gibt Gedanken wieder (Bezug „Gedanke“), aber keinen der Perspektivfigur → am ersten Gedanken („Der
//    Text gibt Gedanken von Ben wieder, Perspektive ist Clara“). Gedanken beider im selben Text sind Head-Hopping, nicht dies.
//  - Head-Hopping (PF 26, Roadmap AP-41): gesetzt, der Draft gibt Gedanken der Perspektivfigur wieder und daneben die einer anderen
//    Person → an jedem fremden Gedanken („Gedanken von Ben, Perspektive ist Clara“). Ein Gedanke, den beide teilen, ist keiner.
// ---------------------------------------------------------------------------

import type { StoryCtx } from './ctx.js';
import { isKnower } from './knowledge.js';
import { draftDocOf, povOf } from './narrative.js';
import { refIndex } from './refs.js';
import { placeLinksIn, placeOf, type PlaceLink } from './scenePlace.js';
import { spansIn } from './spans.js';
import type { IgnoredTerm, StoryNode } from './types.js';

/** Ein Gedanke im Text: wer ihn denkt (Elemente und ihre Schlüssel) */
export interface ThoughtLink {
  entityIds: string[];
  keys: string[];
}

/**
 * Ein Widerspruch zwischen Text und Angabe von Hand, an seiner Stelle `at` (eine Ortsnennung bzw. ein Gedanke); beim Head-Hopping (`hop`)
 * sind `thinkers` die anderen Personen, deren Gedanken an dieser Stelle stehen
 */
export type SettingConflict<P extends { entityId: string; key: string }, T extends ThoughtLink> =
  | { kind: 'place'; at: P; settingId: string }
  | { kind: 'pov' | 'hop'; at: T; settingId: string; thinkers: string[] };

const keyOf = (ctx: StoryCtx, id: string): string => ctx.entities.get(id)?.refKey ?? id;

/** „Ist Absicht“ für einen Widerspruch, gemerkt im Dokument: gilt für genau diese Angabe und diesen Text */
export function settingConflictIgnore(ctx: StoryCtx, c: SettingConflict<{ entityId: string; key: string }, ThoughtLink>): IgnoredTerm {
  if (c.kind === 'place') return { term: '~schauplatz', before: `@${keyOf(ctx, c.settingId)}`, after: `@${c.at.key}` };
  return { term: c.kind === 'hop' ? '~kopfwechsel' : '~perspektive', before: `@${keyOf(ctx, c.settingId)}`, after: [...new Set(c.thinkers.map((id) => keyOf(ctx, id)))].sort().map((k) => `@${k}`).join(' ') };
}

const same = (a: IgnoredTerm, b: IgnoredTerm): boolean => a.term === b.term && a.before === b.before && a.after === b.after;

/**
 * Widersprüche einer Szene zwischen ihren Angaben (Schauplatz, Perspektive) und dem Text, dazu Head-Hopping: `places` sind die verknüpften Orte des Textes,
 * `thoughts` seine Gedanken, je in Textreihenfolge (aus dem Markdown bzw. aus dem Editor). Ohne, was im Dokument als Absicht gemerkt ist.
 */
export function settingConflicts<P extends { entityId: string; key: string }, T extends ThoughtLink>(
  ctx: StoryCtx,
  scene: StoryNode,
  places: readonly P[],
  thoughts: readonly T[],
  ignored?: readonly IgnoredTerm[],
): SettingConflict<P, T>[] {
  const out: SettingConflict<P, T>[] = [];
  const setting = placeOf(ctx, scene);
  if (setting && places.length > 0 && !places.some((p) => p.entityId === setting)) out.push({ kind: 'place', at: places[0]!, settingId: setting });
  const pov = povOf(ctx, scene);
  const thinking = thoughts.filter((t) => t.entityIds.length > 0);
  if (pov && thinking.length > 0 && !thinking.some((t) => t.entityIds.includes(pov))) {
    out.push({ kind: 'pov', at: thinking[0]!, settingId: pov, thinkers: [...new Set(thinking.flatMap((t) => t.entityIds))] });
  } else if (pov) {
    // Head-Hopping: neben Gedanken der Perspektivfigur die einer anderen Person
    for (const t of thinking) {
      const others = t.entityIds.filter((id) => id !== pov && isKnower(ctx, id));
      if (!t.entityIds.includes(pov) && others.length > 0) out.push({ kind: 'hop', at: t, settingId: pov, thinkers: others });
    }
  }
  return out.filter((c) => !ignored?.some((x) => same(x, settingConflictIgnore(ctx, c))));
}

/** Die Gedanken eines Textes (Markdown): Bezüge der Art „Gedanke“ mit den Elementen, die es gibt */
export function thoughtLinksIn(ctx: StoryCtx, text: string): (ThoughtLink & { index: number })[] {
  if (!text.includes(' gedanke}')) return [];
  const refs = refIndex(ctx);
  return spansIn(text)
    .filter((s) => s.role === 'gedanke')
    .map((s) => {
      const known = s.keys.filter((k) => refs.has(k.toLowerCase()));
      return { keys: known, entityIds: known.map((k) => refs.get(k.toLowerCase())!.id), index: s.from };
    });
}

/** Die Widersprüche im gespeicherten Draft einer Szene */
export function draftSettingConflicts(ctx: StoryCtx, scene: StoryNode): SettingConflict<PlaceLink, ThoughtLink & { index: number }>[] {
  const draft = draftDocOf(scene);
  if (!draft || draft.text.trim() === '') return [];
  return settingConflicts(ctx, scene, placeLinksIn(ctx, draft.text), thoughtLinksIn(ctx, draft.text), draft.ignored);
}
